import React, { useState, useRef, useEffect } from "react";
import { FaTachometerAlt, FaSearch, FaBalanceScale, FaStar } from "react-icons/fa";
import CountryList from "../../CountryList";
import { appleApi } from "../../../services/api";

const KeywordDifficulty = () => {
  const [keyword, setKeyword] = useState("");
  const [country, setCountry] = useState("us");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");

  // Comparison
  const [keyword1, setKeyword1] = useState("");
  const [keyword2, setKeyword2] = useState("");
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState(null);
  const [compareStatusMsg, setCompareStatusMsg] = useState("");

  // timers, guards, aborts
  const searchTimers = useRef([]);
  const compareTimers = useRef([]);
  const searchReqId = useRef(0);
  const compareReqId = useRef(0);
  const searchAbortRef = useRef(null);
  const compareAbortRef = useRef(null);

  useEffect(() => {
    const st = searchTimers.current;
    const ct = compareTimers.current;
    const sa = searchAbortRef.current;
    const ca = compareAbortRef.current;
    return () => {
      st.forEach(clearTimeout);
      ct.forEach(clearTimeout);
      if (sa) sa.abort();
      if (ca) ca.abort();
    };
  }, []);

  const startTimedMessages = (setFn, bucketRef) => {
    bucketRef.current.forEach(clearTimeout);
    bucketRef.current = [];
    setFn("Fetching your results…");
    bucketRef.current.push(setTimeout(() => setFn("Still fetching… this may take a little while."), 10_000));
    bucketRef.current.push(setTimeout(() => setFn("Working on it… analyzing many results can be slow."), 30_000));
    bucketRef.current.push(setTimeout(() => setFn("Almost there… large datasets take longer than usual."), 60_000));
  };

  const stopTimedMessages = (setFn, bucketRef) => {
    bucketRef.current.forEach(clearTimeout);
    bucketRef.current = [];
    setFn("");
  };

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setError("Please enter a keyword.");
      return;
    }

    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;

    const myId = ++searchReqId.current;

    setLoading(true);
    setError(null);
    setData(null);
    startTimedMessages(setStatusMsg, searchTimers);

    try {
      const response = await appleApi.get("apple-keyword-difficulty/", {
        params: { keyword: keyword.trim(), country: country || "us" },
        signal: controller.signal,
      });

      if (searchReqId.current === myId) {
        setData(response.data);
      }
    } catch (err) {
      const canceled =
        err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.message === "canceled";
      if (!canceled && searchReqId.current === myId) {
        console.error("Search Error:", err);
        setError("Failed to fetch data. Try again.");
      }
    } finally {
      if (searchReqId.current === myId) {
        setLoading(false);
        stopTimedMessages(setStatusMsg, searchTimers);
        if (searchAbortRef.current === controller) searchAbortRef.current = null;
      }
    }
  };

  const handleCompare = async () => {
    if (!keyword1.trim() || !keyword2.trim()) {
      setCompareError("Please enter both keywords.");
      return;
    }

    if (compareAbortRef.current) compareAbortRef.current.abort();
    const controller = new AbortController();
    compareAbortRef.current = controller;

    const myId = ++compareReqId.current;

    setCompareLoading(true);
    setCompareError(null);
    setCompareData(null);
    startTimedMessages(setCompareStatusMsg, compareTimers);

    try {
      const response = await appleApi.get("apple-keyword-difficulty/", {
        params: { keyword: keyword1.trim(), keyword2: keyword2.trim(), country: country || "us" },
        signal: controller.signal,
      });

      if (compareReqId.current === myId) {
        setCompareData(response.data);
      }
    } catch (err) {
      const canceled =
        err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.message === "canceled";
      if (!canceled && compareReqId.current === myId) {
        console.error("Compare Error:", err);
        setCompareError("Failed to fetch comparison. Try again.");
      }
    } finally {
      if (compareReqId.current === myId) {
        setCompareLoading(false);
        stopTimedMessages(setCompareStatusMsg, compareTimers);
        if (compareAbortRef.current === controller) compareAbortRef.current = null;
      }
    }
  };

  const RenderTable = ({ apps }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">App Name</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviews</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Strength Score</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {apps.map((app, index) => (
            <tr key={app.trackId || index} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
              <td className="px-6 py-4">
                <div className="flex flex-col">
                  <a 
                    href={app.trackViewUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {app.app_name}
                  </a>
                  <span className="text-xs text-gray-400">ID: {app.trackId}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center text-sm text-gray-900">
                  <span className="font-medium mr-1">{app.rating?.toFixed(2)}</span>
                  <FaStar className="text-yellow-400 w-4 h-4" />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {app.rating_count?.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full mr-2 w-24">
                    <div 
                      className={`h-2 rounded-full ${app.strength_0_1 > 0.7 ? 'bg-red-500' : app.strength_0_1 > 0.4 ? 'bg-yellow-500' : 'bg-green-500'}`}
                      style={{ width: `${(app.strength_0_1 || 0) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{(app.strength_0_1 * 100).toFixed(1)}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-8">
      {/* Single Keyword Section */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaTachometerAlt className="mr-3 text-blue-600" />
          Keyword Difficulty Analysis
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Enter Keyword</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Enter keyword"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg py-2.5 pr-4 pl-12 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>
          <div className="md:col-span-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Country</label>
            <CountryList onSelect={(code) => setCountry(code.toLowerCase())} initialCountryCode={country.toUpperCase()} />
          </div>
          <div className="md:col-span-2">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <FaSearch className="mr-2" />
                  Search
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md shadow-sm">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {loading && !data && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-2 text-gray-500 text-sm">{statusMsg}</p>
        </div>
      )}

      {data && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-2">Analysis Results</h2>
            <p className="text-sm text-gray-500 mb-4">
              Results for <strong>{data.keyword}</strong> in <strong>{data.country?.toUpperCase()}</strong>
            </p>
            <div className="flex items-center mb-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <span className="block text-sm text-blue-600 font-semibold mb-1">Difficulty Score</span>
                <span className="text-3xl font-bold text-blue-800">{data.difficulty_score} <span className="text-base text-blue-600 font-normal">/ 100</span></span>
              </div>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
               <RenderTable apps={data.apps_analyzed || []} />
            </div>
          </div>
        </div>
      )}

      {/* Comparison Section */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaBalanceScale className="mr-3 text-green-500" />
          Keyword Comparison
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-3">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Keyword 1</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Keyword 1"
                value={keyword1}
                onChange={(e) => setKeyword1(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg py-2.5 pr-4 pl-12 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              />
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Keyword 2</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Keyword 2"
                value={keyword2}
                onChange={(e) => setKeyword2(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg py-2.5 pr-4 pl-12 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              />
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Country</label>
            <CountryList onSelect={(code) => setCountry(code.toLowerCase())} initialCountryCode={country.toUpperCase()} />
          </div>
          <div className="md:col-span-3">
            <button
              onClick={handleCompare}
              disabled={compareLoading}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {compareLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <FaBalanceScale className="mr-2" />
                  Compare
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {compareError && (
         <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md shadow-sm">
           <p className="text-sm text-red-700">{compareError}</p>
         </div>
      )}

      {compareLoading && !compareData && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-green-500 border-t-transparent"></div>
          <p className="mt-2 text-gray-500 text-sm">{compareStatusMsg}</p>
        </div>
      )}

      {compareData && (
        <div className="space-y-8">
           {/* If the API returns 'comparison' object or array, we handle it here. 
               Assuming it returns an object keyed by keyword or an array of result objects 
               based on Google implementation which returns { comparison: { kw1: {...}, kw2: {...} } }
           */}
           {compareData.comparison ? (
             Object.entries(compareData.comparison).map(([key, val], idx) => (
                <div key={idx} className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">
                        {key} <span className="text-sm font-normal text-gray-500 ml-2">Difficulty: <span className={val.difficulty_score > 70 ? "text-red-500 font-bold" : "text-green-600 font-bold"}>{val.difficulty_score}</span>/100</span>
                    </h3>
                    <RenderTable apps={val.apps_analyzed || []} />
                </div>
             ))
           ) : (
             /* Fallback if structure is different, e.g. if it just returns array of 2 results */
             Array.isArray(compareData) && compareData.map((val, idx) => (
                <div key={idx} className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">
                        {val.keyword} <span className="text-sm font-normal text-gray-500 ml-2">Difficulty: <span className={val.difficulty_score > 70 ? "text-red-500 font-bold" : "text-green-600 font-bold"}>{val.difficulty_score}</span>/100</span>
                    </h3>
                    <RenderTable apps={val.apps_analyzed || []} />
                </div>
             ))
           )}
        </div>
      )}
    </div>
  );
};

export default KeywordDifficulty;
